/**
 * Entrada: teclado, mouse e pointer lock.
 *
 * Consumo pelos sistemas:
 *   input.isDown('forward')      -> estado continuo da acao
 *   input.wasPressed('reload')   -> disparo unico neste frame (borda de subida)
 *   input.consumeMouseDelta()    -> { x, y } acumulado desde o ultimo frame
 *
 * Importante: wasPressed()/consumeMouseDelta() sao zerados por input.endFrame(),
 * chamado pelo Engine no fim de cada frame. Nenhum sistema deve chamar endFrame().
 *
 * FONTE VIRTUAL (controles de toque)
 * ---------------------------------
 * Os campos `virtual*` abaixo sao um segundo produtor de entrada, alimentado pelo
 * src/ui/TouchControls.js. Eles sao somados aos do teclado/mouse na LEITURA, e nao
 * na escrita: assim o resto do jogo continua chamando isDown('forward') sem nunca
 * saber se aquilo veio de uma tecla ou de um polegar. Foi por isso que o celular
 * nao exigiu tocar em PlayerController, CameraRig nem WeaponSystem.
 *
 * Quem dispensa o pointer lock no celular sao os proprios campos virtuais:
 * isMouseDown() devolve true assim que `virtualButtons[b]` estiver marcado, ANTES
 * de testar `locked`. Sem isso o botao de atirar da tela seria lido como "mouse
 * solto" e o jogo ficaria sem tiro no celular.
 *
 * `touchActive` e outra coisa: e a bandeira que diz "a entrada esta vindo do
 * dedo agora". Ela NAO participa da leitura de entrada - serve para quem decide
 * PEDIR o pointer lock (Menu, Lobby, placar final) saber que nao deve pedir.
 */

export const DEFAULT_BINDINGS = {
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space'],
  sprint: ['ShiftLeft', 'ShiftRight'],
  crouch: ['ControlLeft', 'KeyC'],
  reload: ['KeyR'],
  interact: ['KeyF'],
  melee: ['KeyV'],
  grenade: ['KeyG'],
  flashlight: ['KeyL'],
  weapon1: ['Digit1'],
  weapon2: ['Digit2'],
  weapon3: ['Digit3'],
  // O arsenal passou de 3 para 7 armas. Sem estas quatro teclas, bk12, px5, lm8
  // e mk4 existiam no codigo e eram INALCANCAVEIS pelo jogador - o Q so alterna
  // entre as duas ultimas usadas, ele nao percorre a lista.
  weapon4: ['Digit4'],
  weapon5: ['Digit5'],
  weapon6: ['Digit6'],
  weapon7: ['Digit7'],
  nextWeapon: ['KeyQ'],
  /** Percorre o arsenal em ordem. E o caminho do celular e da roda do mouse. */
  cycleWeapon: ['KeyX'],
  pause: ['Escape'],
  scoreboard: ['Tab']
};

/** O foco esta num lugar onde a tecla pertence ao texto, e nao ao jogo? */
function isTextField(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return el.isContentEditable === true;
}

export class Input {
  constructor(canvas, settings) {
    this.canvas = canvas;
    this.settings = settings;
    this.bindings = { ...DEFAULT_BINDINGS };

    this._down = new Set();        // codes fisicos pressionados
    this._pressed = new Set();     // codes que desceram neste frame
    this._released = new Set();
    this._mouse = { x: 0, y: 0 };  // delta acumulado do frame
    this._wheel = 0;
    this.mouseButtons = [false, false, false];
    this._mousePressed = [false, false, false];
    this.locked = false;
    this.enabled = true;

    // --- fonte virtual (preenchida pelo TouchControls; ver cabecalho) --------
    /** @type {Set<string>} acoes seguradas pelo dedo neste instante */
    this.virtualActions = new Set();
    /** @type {Set<string>} acoes que desceram neste frame */
    this.virtualPressed = new Set();
    this.virtualButtons = [false, false, false];
    this.virtualPressedButtons = [false, false, false];
    /** Delta de olhar em PIXELS, mesma unidade de movementX/movementY do mouse. */
    this.virtualLook = { x: 0, y: 0 };
    /** Liga a leitura da fonte virtual e dispensa o pointer lock. */
    this.touchActive = false;

    this._handlers = [];
    this._bind();
  }

  _add(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this._handlers.push(() => target.removeEventListener(type, fn, opts));
  }

  _bind() {
    this._add(window, 'keydown', (e) => {
      if (e.repeat) return;
      // FOCO EM CAMPO DE TEXTO: o jogo nao le nada e nao bloqueia nada. Sem esta
      // saida, o preventDefault abaixo impedia Tab entre campos e ESPACO dentro
      // dos formularios de conta e de sala - digitar "Joao Silva" no cadastro
      // simplesmente nao funcionava.
      if (isTextField(document.activeElement)) return;
      // Tab e Space rolam a pagina; F5/F12 ficam livres para o desenvolvedor
      if (['Tab', 'Space', 'ArrowUp', 'ArrowDown'].includes(e.code)) e.preventDefault();
      this._down.add(e.code);
      this._pressed.add(e.code);
    });

    this._add(window, 'keyup', (e) => {
      this._down.delete(e.code);
      this._released.add(e.code);
    });

    this._add(window, 'blur', () => {
      this._down.clear();
      this.mouseButtons = [false, false, false];
      // O dedo tambem "solta" ao perder o foco: sem isto, trocar de aba com o
      // joystick pressionado deixa o jogador andando sozinho ao voltar.
      this.clearVirtual();
    });

    this._add(this.canvas, 'mousedown', (e) => {
      if (!this.locked) return;
      if (e.button < 3) {
        this.mouseButtons[e.button] = true;
        this._mousePressed[e.button] = true;
      }
    });

    this._add(window, 'mouseup', (e) => {
      if (e.button < 3) this.mouseButtons[e.button] = false;
    });

    this._add(window, 'mousemove', (e) => {
      if (!this.locked || !this.enabled) return;
      this._mouse.x += e.movementX || 0;
      this._mouse.y += e.movementY || 0;
    });

    this._add(window, 'wheel', (e) => {
      if (!this.locked) return;
      this._wheel += Math.sign(e.deltaY);
    }, { passive: true });

    this._add(document, 'pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.canvas;
      if (!this.locked) {
        this._down.clear();
        this.mouseButtons = [false, false, false];
      }
      this.onLockChange?.(this.locked);
    });

    this._add(document, 'contextmenu', (e) => {
      if (this.locked) e.preventDefault();
    });
  }

  async requestLock() {
    try {
      const p = this.canvas.requestPointerLock({ unadjustedMovement: true });
      if (p && typeof p.catch === 'function') await p.catch(() => this.canvas.requestPointerLock());
    } catch {
      try { this.canvas.requestPointerLock(); } catch { /* ignorado */ }
    }
  }

  releaseLock() {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  _codesOf(action) {
    return this.bindings[action] || [];
  }

  isDown(action) {
    if (!this.enabled) return false;
    if (this.virtualActions.has(action)) return true;
    return this._codesOf(action).some((c) => this._down.has(c));
  }

  wasPressed(action) {
    if (!this.enabled) return false;
    if (this.virtualPressed.has(action)) return true;
    return this._codesOf(action).some((c) => this._pressed.has(c));
  }

  wasReleased(action) {
    return this._codesOf(action).some((c) => this._released.has(c));
  }

  /** Botao 0 = atirar, 1 = melee, 2 = mira (ADS). */
  isMouseDown(button) {
    if (!this.enabled) return false;
    if (this.virtualButtons[button]) return true;
    return this.locked && !!this.mouseButtons[button];
  }

  wasMousePressed(button) {
    if (!this.enabled) return false;
    if (this.virtualPressedButtons[button]) return true;
    return this.locked && !!this._mousePressed[button];
  }

  /**
   * Delta do mouse do frame, ja multiplicado pela sensibilidade e pelo invertY.
   * Chamar UMA vez por frame (o CameraRig e o dono).
   */
  consumeMouseDelta(sensitivityScale = 1) {
    const s = this.settings.sensitivity * sensitivityScale;
    // O arrasto do dedo entra na MESMA unidade do mouse (pixels) e passa pela
    // mesma sensibilidade - o ajuste do menu vale para os dois sem duplicar nada.
    const dx = this._mouse.x + this.virtualLook.x;
    const dy = this._mouse.y + this.virtualLook.y;
    const out = {
      x: dx * s,
      y: dy * s * (this.settings.invertY ? -1 : 1)
    };
    this._mouse.x = 0;
    this._mouse.y = 0;
    this.virtualLook.x = 0;
    this.virtualLook.y = 0;
    return out;
  }

  consumeWheel() {
    const w = this._wheel;
    this._wheel = 0;
    return w;
  }

  /** Chamado pelo Engine no fim do frame. */
  endFrame() {
    this._pressed.clear();
    this._released.clear();
    this._mousePressed[0] = this._mousePressed[1] = this._mousePressed[2] = false;
    // A borda de subida virtual dura UM frame, igual a do teclado. O estado
    // continuo (virtualActions / virtualButtons) NAO e limpo aqui: quem solta
    // e o dedo, no pointerup.
    this.virtualPressed.clear();
    this.virtualPressedButtons[0] = false;
    this.virtualPressedButtons[1] = false;
    this.virtualPressedButtons[2] = false;
  }

  /** Solta tudo que veio do toque. Usado no blur e ao esconder os controles. */
  clearVirtual() {
    this.virtualActions.clear();
    this.virtualPressed.clear();
    this.virtualButtons[0] = this.virtualButtons[1] = this.virtualButtons[2] = false;
    this.virtualPressedButtons[0] = false;
    this.virtualPressedButtons[1] = false;
    this.virtualPressedButtons[2] = false;
    this.virtualLook.x = 0;
    this.virtualLook.y = 0;
  }

  dispose() {
    for (const off of this._handlers) off();
    this._handlers.length = 0;
  }
}
